/** Адрес карточки в строке браузера — без библиотеки маршрутизации.
 *
 * Своего роутера в проекте нет, а ставить его ради одного адреса — это
 * зависимость, новая сборка и чужие соглашения там, где хватает двадцати
 * строк на History API.
 *
 * Адрес нужен ради ссылки: на сделку ссылаются в задаче, в переписке с
 * производством, просто кидают коллеге. Модальное окно такой ссылки не
 * даёт, поэтому карточка и переезжает на свой адрес.
 *
 * nginx уже отдаёт index.html на любой путь (`nginx-spa.conf`), так что
 * прямая ссылка и обновление страницы работают.
 */
import { useState, useEffect, useCallback } from 'react';

const LEAD = /^\/lead\/([A-Za-z0-9-]+)\/?$/;

export function leadIdFromPath(pathname = window.location.pathname) {
  const m = LEAD.exec(pathname);
  return m ? m[1] : null;
}

export function leadUrl(id) {
  return `/lead/${id}`;
}

/** Идентификатор сделки из адреса; следит за кнопками «назад» и «вперёд». */
export function useLeadRoute() {
  const [leadId, setLeadId] = useState(() => leadIdFromPath());

  useEffect(() => {
    const onPop = () => {
      setLeadId(leadIdFromPath());
      setCameFrom((window.history.state || {}).from || null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const [cameFrom, setCameFrom] = useState(() => (window.history.state || {}).from || null);

  const openLead = useCallback((id, from = null) => {
    if (!id) return;
    // pushState, а не replace: «назад» должен возвращать на доску, это
    // первое, что пробует человек, закрывая карточку.
    //
    // Откуда пришли, помним здесь же. Кнопка возврата делает
    // history.back() и честно отдаёт назад — в «Клиенты», если карточку
    // открыли оттуда, — но называлась она всегда «К доске». Человек
    // нажимал «К доске» и оказывался в списке клиентов.
    window.history.pushState({ leadId: id, from }, '', leadUrl(id));
    setLeadId(id);
    setCameFrom(from);
  }, []);

  const closeLead = useCallback(() => {
    // Если пришли с доски — возвращаемся назад по истории, чтобы не
    // копить записи. Если открыли по прямой ссылке, истории нет, и
    // тогда просто встаём на корень.
    if (window.history.state && window.history.state.leadId) {
      window.history.back();
    } else {
      window.history.pushState({}, '', '/');
      setLeadId(null);
    }
  }, []);

  return { leadId, openLead, closeLead, cameFrom };
}
